import { Alert, Anchor, Badge, Button, Card, Code, Group, Modal, Stack, Table, Text, Textarea, ThemeIcon, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCheck, IconMinus, IconPencil, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { useMe } from '../../lib/me';
import type { CourseVersion } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';
import { VersionPreview } from './VersionEditor';

interface QueueItem { id: number; courseId: number; title: string; version: number; submittedAt: string; approvalDueAt: string; overdue: boolean; submittedBy: number; submittedByName: string; createdBy: number; lessons: number; questions: number }
interface Diff { previousVersion: number | null; changes: { kind: 'added' | 'removed' | 'changed' | 'new'; text: string; before?: string; after?: string }[] }

export function ApprovalsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['approvals'], queryFn: () => api.get<QueueItem[]>('/admin/content/approvals') });
  return (
    <>
      <PageHeader title="Контент: одобрение" subtitle="Версии ждут проверки вторым человеком с ролью «Контент». Срок одобрения - 3 рабочих дня. Свою версию одобрить нельзя." />
      <QueryState loading={q.isLoading} error={q.error}>
        {() => (
          <Card withBorder radius="lg" p={0}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead><Table.Tr><Table.Th>Курс</Table.Th><Table.Th>Автор</Table.Th><Table.Th>Отправлена</Table.Th><Table.Th>Одобрить до</Table.Th><Table.Th /></Table.Tr></Table.Thead>
              <Table.Tbody>
                {q.data!.map((v) => {
                  const own = v.submittedBy === me.id || v.createdBy === me.id;
                  return (
                    <Table.Tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/approvals/${v.id}`)}>
                      <Table.Td><Text fw={500} size="sm">{v.title}</Text><Text size="xs" c="dimmed">v{v.version} · {v.lessons} урока · {v.questions} вопросов</Text></Table.Td>
                      <Table.Td><Text size="sm">{v.submittedByName}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDateTime(v.submittedAt)}</Text></Table.Td>
                      <Table.Td><Text size="sm" c={v.overdue ? 'red' : undefined} fw={v.overdue ? 600 : 400}>{fmtDate(v.approvalDueAt)}{v.overdue && ' · просрочено'}</Text></Table.Td>
                      <Table.Td>{own ? <Badge color="gray" variant="outline">ваша версия</Badge> : <Badge color="violet" variant="light">проверить</Badge>}</Table.Td>
                    </Table.Tr>
                  );
                })}
                {q.data!.length === 0 && <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" size="sm" ta="center" py="md">Очередь пуста</Text></Table.Td></Table.Tr>}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </QueryState>
    </>
  );
}

const KIND = {
  added: { icon: IconPlus, color: 'teal', label: 'Добавлено' },
  removed: { icon: IconMinus, color: 'red', label: 'Удалено' },
  changed: { icon: IconPencil, color: 'blue', label: 'Изменено' },
  new: { icon: IconPlus, color: 'violet', label: 'Новый курс' },
} as const;

export function ApprovalReviewPage() {
  const versionId = Number(useParams().versionId);
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [returnOpen, ret] = useDisclosure(false);
  const [comment, setComment] = useState('');
  const v = useQuery({ queryKey: ['version', versionId], queryFn: () => api.get<CourseVersion>(`/admin/content/versions/${versionId}`) });
  const diff = useQuery({ queryKey: ['diff', versionId], queryFn: () => api.get<Diff>(`/admin/content/versions/${versionId}/diff`) });
  const done = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: ['approvals'] });
    await qc.invalidateQueries({ queryKey: ['version', versionId] });
    await qc.invalidateQueries({ queryKey: ['content-courses'] });
    notifications.show({ color: 'teal', message: msg });
    navigate('/admin/approvals');
  };
  const approve = useMutation({ mutationFn: () => api.post(`/admin/content/versions/${versionId}/approve`, {}), onSuccess: () => done('Версия одобрена и опубликована'), onError: (e: Error) => notifications.show({ color: 'red', message: e.message }) });
  const back = useMutation({ mutationFn: () => api.post(`/admin/content/versions/${versionId}/return`, { comment }), onSuccess: () => done('Версия возвращена автору'), onError: (e: Error) => notifications.show({ color: 'red', message: e.message }) });

  return (
    <QueryState loading={v.isLoading} error={v.error}>
      {() => {
        const ver = v.data!;
        const own = ver.submittedBy === me.id || ver.createdBy === me.id;
        return (
          <Stack>
            <Anchor component={Link} to="/admin/approvals" size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> Очередь одобрения</Anchor>
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="sm" c="dimmed">v{ver.version} · автор {ver.submittedByName} · отправлена {fmtDateTime(ver.submittedAt)} · одобрить до {fmtDate(ver.approvalDueAt)}</Text>
                <Title order={2}>{ver.title}</Title>
              </div>
              {ver.status === 'ON_APPROVAL' && !own && (
                <Group gap="xs">
                  <Button variant="default" color="orange" onClick={ret.open}>Вернуть с комментарием</Button>
                  <Button color="teal" leftSection={<IconCheck size={16} />} loading={approve.isPending} onClick={() => approve.mutate()}>Одобрить и опубликовать</Button>
                </Group>
              )}
            </Group>
            {own && <Alert color="gray">Это ваша версия: одобрить или вернуть её может только другой человек с ролью «Контент».</Alert>}
            {ver.status !== 'ON_APPROVAL' && <Alert color="gray">Версия уже не на одобрении.</Alert>}
            {ver.problems.length > 0 && <Alert color="orange" title="Нарушены правила курса">{ver.problems.join('; ')}</Alert>}
            <Card withBorder radius="lg" padding="lg">
              <Title order={4} mb="sm">Сравнение {diff.data?.previousVersion ? `с опубликованной v${diff.data.previousVersion}` : ''}</Title>
              <QueryState loading={diff.isLoading} error={diff.error}>
                {() => (
                  <Stack gap="sm">
                    {diff.data!.changes.length === 0 && <Text size="sm" c="dimmed">Изменений нет</Text>}
                    {diff.data!.changes.map((c, i) => {
                      const k = KIND[c.kind];
                      return (
                        <Group key={i} align="flex-start" gap="sm" wrap="nowrap">
                          <ThemeIcon size={22} radius="xl" color={k.color} variant="light"><k.icon size={13} /></ThemeIcon>
                          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm"><b>{k.label}:</b> {c.text}</Text>
                            {c.before !== undefined && (
                              <Group grow align="flex-start" gap="xs">
                                <Code block style={{ whiteSpace: 'pre-wrap', background: 'var(--mantine-color-red-light)', maxHeight: 220, overflow: 'auto' }}>{c.before || '—'}</Code>
                                <Code block style={{ whiteSpace: 'pre-wrap', background: 'var(--mantine-color-teal-light)', maxHeight: 220, overflow: 'auto' }}>{c.after || '—'}</Code>
                              </Group>
                            )}
                          </Stack>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </QueryState>
            </Card>
            <Title order={4}>Версия целиком</Title>
            <VersionPreview v={ver} />
            <Modal opened={returnOpen} onClose={ret.close} title="Вернуть на доработку" centered>
              <Stack>
                <Textarea label="Что исправить" autosize minRows={3} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={ret.close}>Отмена</Button>
                  <Button color="orange" disabled={!comment.trim()} loading={back.isPending} onClick={() => back.mutate()}>Вернуть</Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        );
      }}
    </QueryState>
  );
}
